/**
 * URL State Management
 * 
 * Handles synchronization between browser URL hash and application space state.
 * Uses hash-based routing for compatibility with static hosting.
 */

/**
 * Extract spaceId from URL hash
 * Format: #spaceId or #/spaceId
 * @returns spaceId or null if not present
 */
export function getSpaceIdFromUrl(): string | null {
    const hash = window.location.hash;
    if (!hash || hash === '#') {
        return null;
    }
    
    // Remove leading # and optional /
    const cleaned = hash.replace(/^#\/?/, '');
    
    return cleaned || null;
}

/**
 * Update URL hash with spaceId without triggering page reload
 * @param spaceId - The space ID to set in the URL
 */
export function updateUrlWithSpaceId(spaceId: string): void {
    const newHash = `#${spaceId}`;
    
    // Only update if different to avoid unnecessary history entries
    if (window.location.hash !== newHash) {
        window.location.hash = newHash;
    }
}

/**
 * Listen for URL hash changes (browser back/forward navigation)
 * @param callback - Called when hash changes with the new spaceId (or null)
 * @returns Cleanup function to remove the listener
 */
export function onSpaceIdChange(callback: (spaceId: string | null) => void): () => void {
    const handleHashChange = () => {
        const spaceId = getSpaceIdFromUrl();
        callback(spaceId);
    };
    
    window.addEventListener('hashchange', handleHashChange);
    
    // Return cleanup function
    return () => {
        window.removeEventListener('hashchange', handleHashChange);
    };
}
